import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { OrganizationService } from '../../../../core/services/organization.service';

import { MultipleMessageComponent } from '../multiple-message/multiple-message.component';
import { User } from '../../../../core/models/organization.interface';
import { ArcAngelPapiService } from '../../../../core/services/arc-angel-papi.service';
import { MessageComponent } from '../../message/message.component';
import { PhoneboxComponent } from '../../phonebox/phonebox.component';
import { catchError, map, of, switchMap } from 'rxjs';
import { CameraService } from '../../../../core/services/camera.service';

@Component({
  selector: 'app-response-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './details-modal.component.html',
  styleUrl: './details-modal.component.css'
})
export class DetailsModalComponent implements OnInit {
  message: any;
  title: string = '';
  org: any; // Make sure this is a public property
  userInfo: any; // Added to fix the error
  suspectData: any;
  public selectedTab: string = 'basic';
  shouldCallApi: boolean = false;
  @Input() showCancel: boolean = false;
  @Input() users: User[] = [];
  userId: any;
  userPictureUrl: any;
  @Output() userInfoRequested = new EventEmitter<User>();

  constructor(
    public activeModal: NgbActiveModal,
    public organizationService: OrganizationService,
    public modalService: NgbModal,
    private arcAngelPapiService: ArcAngelPapiService,
    private camera: CameraService
  ) { }

  ngOnInit(): void {
    if (!this.shouldCallApi) {
      this.userId = this.userInfo?.userId;
      this.organizationService.getProfilesFromOrganization(this.userId)
        .subscribe({
          next: (result: any) => {
            const profileResults = result;
            this.userInfo = profileResults;
            if (profileResults.UserPictures.length > 0) {
              this.organizationService.getUserPicture(profileResults.UserPictures[0].id)
                .subscribe({
                  next: (result: any) => {
                    if (!result) {
                      return;
                    }
                    const contentType =
                      (result?.headers && typeof result.headers === 'object'
                        ? result.headers['content-type'] || result.headers['Content-Type']
                        : undefined) || 'application/octet-stream';
                    const blob = new Blob([result], { type: contentType });
                    let reader = new FileReader();

                    reader.addEventListener('load', () => {
                      this.userInfo.picture = reader.result;
                      
                    });

                    reader.readAsDataURL(blob);
                  },
                  error: (error: any) => { }
                });
            }
          },
          error: (error: any) => { }
        });
    }
  }
  get activityList(): any[] {
    return this.suspectData ? [this.suspectData] : [];
  }

  public openMultipleMessageModal(
    title: string,
    user: any,
    showCancel: boolean = false
  ) {
    this.organizationService.getProfilesFromOrganization(user.id).subscribe({
      next: (userInfo: any) => {
        const modalRef = this.modalService.open(MultipleMessageComponent, {
          size: 'md',
          centered: true,
          backdrop: 'static',
          keyboard: false,
          windowClass: 'custom-modal',
        });
        modalRef.componentInstance.title = title;
        modalRef.componentInstance.userInfo = userInfo;
        modalRef.componentInstance.showCancel = showCancel;
        modalRef.componentInstance.users = this.users; // <-- ADD THIS LINE
        return modalRef.result;
      },
      error: (err: any) => {
        console.error('Error fetching user profile:', err);
      }
    });
  }

  fetchProfilesFromOrganization(user: User, event: Event, status: boolean): void {
    event.stopPropagation();

    const title = 'User Details';
    this.organizationService.getProfilesFromOrganization(user.id).pipe(
      switchMap((userInfo: any) => {
        if (!userInfo) {
          return of({ userInfo: null, pictureBlob: null, emergencyNumber: null });
        }

        const pictureId = userInfo?.UserPictures?.[0]?.id || null;
        const organizationId = userInfo?.UserOrganizations?.[0]?.Organization?.id || null;

        return this.organizationService.getEmergencyNumber().pipe(
          switchMap((emergencyNumbers: any) => {
            const emergencyNumber = this.findEmergencyNumber(emergencyNumbers, organizationId);

            if (!pictureId) {
              return of({ userInfo, pictureBlob: null, emergencyNumber });
            }

            return this.organizationService.getUserPicture(pictureId).pipe(
              catchError(error => {
                return of(null);
              }),
              map(pictureBlob => ({ userInfo, pictureBlob, emergencyNumber }))
            );
          })
        );
      })
    ).subscribe({
      next: ({ userInfo, pictureBlob, emergencyNumber }) => {
        if (!userInfo) return;

        userInfo.picture = pictureBlob ? URL.createObjectURL(pictureBlob) : null;
        userInfo.emergencyNumber = emergencyNumber;

        if (status) {
          this.openDetailsModal(title, userInfo, true);
          return;
        }

        this.handleFlyToUserLocation(userInfo);
      },
      error: (err: any) => {
        console.error('Error fetching user data:', err);
        this.errorModal('Error', 'Failed to fetch user data.', true);
      },
      complete: () => {
      }
    });
  }

  private handleFlyToUserLocation(userInfo: any): void {
    const coordinates = userInfo?.UserAddresses?.[0]?.position?.coordinates;

    if (Array.isArray(coordinates) && coordinates.length === 2) {
      const [longitude, latitude] = coordinates;
      this.camera.flyToLocation(latitude, longitude, 500);
      this.userInfoRequested.emit(userInfo);
    } else {
      this.errorModal('Location unknown', 'Location unknown', true);
    }
  }

  private openDetailsModal(
    title: string,
    data: any,
    shouldCallApi: boolean
  ) {
    const modalRef = this.modalService.open(DetailsModalComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      windowClass: 'response-modal-top zindex',
      backdropClass: 'response-modal-backdrop-top',
      keyboard: false,
    });

    modalRef.componentInstance.title = title;
    modalRef.componentInstance.userInfo = data;
    modalRef.componentInstance.showCancel = false;
    modalRef.componentInstance.shouldCallApi = shouldCallApi;


    return modalRef.result;
  }
  private errorModal(title: string, data: any, showCancel: boolean = false) {
    const modalRef = this.modalService.open(DetailsModalComponent, {
      size: 'md',
      centered: true,
      backdrop: true,
      windowClass: 'response-modal-top zindex',
      backdropClass: 'response-modal-backdrop-top',
    });

    modalRef.componentInstance.title = title;
    modalRef.componentInstance.message = data;
    modalRef.componentInstance.showCancel = showCancel;

    return modalRef.result;
  }

  private findEmergencyNumber(emergencyNumbers: any, organizationId: string | null): string | null {
    if (
      emergencyNumbers &&
      Array.isArray(emergencyNumbers.rows) &&
      organizationId
    ) {
      const match = emergencyNumbers.rows.find((item: any) => item.organizationId === organizationId);
      return match?.number || null;
    }
    return null;
  }

  openChatWindow(title: string, user: any, isNew: boolean) {
    const modalRef = this.modalService.open(MessageComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      windowClass: 'custom-modal',
    });
    const userInfo = {
      userId : user.id,
      name : user.name,
      username : user.username
    }
    modalRef.componentInstance.userDetailsFromUserInfo = userInfo;
    modalRef.componentInstance.title = title;
  }

  openPhoneWindow(event: Event, user: any): void {
    // Prevent default click behavior if needed
    event.preventDefault();
    event.stopPropagation();

    const modalRef = this.modalService.open(PhoneboxComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      windowClass: 'custom-modal',
    });

    // set inputs for the PhoneboxComponent
    modalRef.componentInstance.user = user;
    modalRef.componentInstance.phone = user?.UserPhones?.[0]?.number ?? '';
    modalRef.componentInstance.toUserFullName = user?.name ?? '';
    modalRef.componentInstance.toCompanyName = user?.UserOrganizations[0]?.Organization?.name ?? '';
  }

  hasMobilePhone(user: any): boolean {
    if (!user?.UserPhones || user.UserPhones.length === 0) return false;

    // Check if there is at least one valid number that looks like a mobile
    return user.UserPhones.some((p: any) =>
      !!p.number && p.number.trim() !== '' && p.type?.toLowerCase() === 'mobile'
    );
  }


}
